import * as React from "react";
import FilePreview, { PreviewNote, type PreviewState } from "pages/GenericOrderDetailsPage/components/FilePreview";
import { ChevronLeft, ChevronRight, Plus, RefreshCcw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useParams } from "react-router-dom";
import { fileToDataUrl as convertFileToBase64 } from "service/moneeflo/legacy-order-api";
import { Img, Spinner } from "components";
import RequestOrderDoc from "./request_doc";
import { toast } from "sonner";
import { checkLevel } from "common/rules";
import { LEVEL_2 } from "common/constants";

interface Document {
  id: string;
  name: string;
  type: "pdf" | "image";
  status: "Unverified" | "Verified" | "Requested";
  url: string;
}

interface DocumentReviewProps {
  documents: Document[];
  onApprove?: (id: string, name: string, file: File | null) => Promise<void>;
  onRequest?: (id: number | string) => Promise<void> | void;
  onReUpload: (id: string, file: string, name: string) => Promise<void>;
  onAddDocument?: (name: string) => Promise<void>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  refetchOrderDetails: () => void;
  index?: number;
  onSubmit?: (id: number) => Promise<void> | void;
  /** When given, a document can be rejected, with the reason it is rejected for. */
  onReject?: (id: string, reason: string) => Promise<void>;
  /**
   * Whether the person may replace a verified document. Left out, a verified document's
   * Re-Upload stays disabled as before; false hides it; true enables it.
   */
  canReplaceVerified?: boolean;
  /** Whether the person may approve a document. Left out, Approve is shown; false hides it. */
  canApprove?: boolean;
  handleAddAdditionalDocument?: (docs: 
    {
    id: string;
    name: string;
    file: File | null;
  }[]) => Promise<void>;
}

export default function DocumentReview({
  index = 0,
  documents,
  onApprove,
  onRequest,
  onReUpload,
  onAddDocument,
  isOpen,
  setIsOpen,
  refetchOrderDetails,
  onSubmit,
  onReject,
  canReplaceVerified,
  canApprove,
  handleAddAdditionalDocument,
}: Readonly<DocumentReviewProps>) {
  const [rejecting, setRejecting] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState("");
  const [currentDocIndex, setCurrentDocIndex] = React.useState(() => index);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isAddingDoc, setIsAddingDoc] = React.useState(false);
  const [newDocumentName, setNewDocumentName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loadedFile, setLoadedFile] = React.useState<File | null>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const wasOpenRef = React.useRef(false);
  if (isOpen && !wasOpenRef.current) openerRef.current = document.activeElement as HTMLElement | null;
  wasOpenRef.current = isOpen;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [docs, setDocs] = React.useState<{
    id: string;
    name: string;
    file: File | null;
  }[]>([]);

  const currentDoc = documents[currentDocIndex];
  const { id } = useParams();
  // Approve waits until the file has actually been shown, so nobody approves what they have not seen.
  const [previewState, setPreviewState] = React.useState<{ key: string; state: PreviewState }>({ key: "", state: "loading" });
  const previewKey = `${currentDoc?.id}:${currentDoc?.url ?? ""}`;
  // A file that cannot be shown, or has no address, never counts as seen.
  const previewLoaded = previewState.key === previewKey && previewState.state === "loaded";

  const handlePrevious = () => {
    if (currentDocIndex > 0) {
      setCurrentDocIndex(currentDocIndex - 1);
      setCurrentPage(1);
    }
  };

  const handleNext = () => {
    if (currentDocIndex < documents.length - 1) {
      setCurrentDocIndex(currentDocIndex + 1);
      setCurrentPage(1);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await onApprove?.(currentDoc?.id, currentDoc?.name, loadedFile as File);
      if (currentDocIndex < documents.length - 1) {
        setCurrentDocIndex(currentDocIndex + 1);
        setLoadedFile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async () => {
    setLoading(true);
    try {
      await onRequest?.(currentDoc?.id as string);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await onReject?.(currentDoc?.id, rejectReason.trim());
      setRejecting(false);
      setRejectReason("");
    } catch {
      // The caller has already said why it failed; the reason stays for another try.
    } finally {
      setLoading(false);
    }
  };

  const handleReUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.size >= 5 * 1024 * 1024) {
      toast.error("File size should be less than or equal to 5MB");
      return;
    }
    const base64 = await convertFileToBase64(file as File);
    if (file) {
      setLoading(true);
      try {
        await onReUpload(currentDoc?.id, base64, currentDoc?.name);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddDocument = async () => {
    if (!newDocumentName.trim()) {
      toast.error("Please enter a document name");
      return;
    }
    
    setLoading(true);
    try {
      await onAddDocument?.(newDocumentName.trim());
      setNewDocumentName("");
      setIsAddingDoc(false);
      toast.success("Document added successfully");
      refetchOrderDetails();
    } catch (error) {
      toast.error("Failed to add document");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAddDocument = () => {
    setNewDocumentName("");
    setIsAddingDoc(false);
  };

  const allDocsVerified = documents?.every((doc) => doc?.status.toLowerCase() === "verified");

  // Add Document Name Input Component
  

  return (
    <>
      {loading && <Spinner />}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-black-900" />
          <DialogContent className="max-w-4xl flex flex-col gap-0 p-0 max-h-[92vh] overflow-hidden bg-white-A700 rounded-md"
 onCloseAutoFocus={(e) => { if (openerRef.current?.isConnected) { e.preventDefault(); openerRef.current.focus(); } }}>
            <DialogHeader className="flex shrink-0 flex-row items-center justify-between px-4 pb-3 pt-6">
              <DialogTitle>
                {checkLevel(LEVEL_2)
                  ? "Re-Upload Documents"
                  : "Review Documents"}
              </DialogTitle>
              <DialogClose asChild>Close</DialogClose>
            </DialogHeader>
            <div className="grid min-h-0 flex-1 grid-cols-[300px_1fr] md:grid-cols-1 gap-4 overflow-y-auto px-4 pb-2">
              {/* Sidebar */}
              <div className="space-y-2">
                {documents.map((doc, index) => (
                  <div
                    role="button"
                    key={doc.id}
                    className={cn(
                      "flex items-center justify-between p-2 rounded-lg cursor-pointer",
                      currentDocIndex === index && "bg-muted text-blue-700",
                    )}
                    onClick={() => {
                      if (loadedFile) {
                        setLoadedFile(null);
                      }
                      setCurrentDocIndex(index);
                      setIsAddingDoc(false); // Close add document form when selecting existing doc
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium font-inter leading-[16.9px] border rounded-md py-1 px-2.5 border-[#EFF4FF]">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium font-inter leading-[16.9px]">
                        {doc.name}
                      </span>
                    </div>
                    {doc.status.toLowerCase() === "verified" && (
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.76758 0.5C3.46158 0.5 0.767578 3.194 0.767578 6.5C0.767578 9.806 3.46158 12.5 6.76758 12.5C10.0736 12.5 12.7676 9.806 12.7676 6.5C12.7676 3.194 10.0736 0.5 6.76758 0.5ZM9.63558 5.12L6.23358 8.522C6.14958 8.606 6.03558 8.654 5.91558 8.654C5.79558 8.654 5.68158 8.606 5.59758 8.522L3.89958 6.824C3.72558 6.65 3.72558 6.362 3.89958 6.188C4.07358 6.014 4.36158 6.014 4.53558 6.188L5.91558 7.568L8.99958 4.484C9.17358 4.31 9.46158 4.31 9.63558 4.484C9.80958 4.658 9.80958 4.94 9.63558 5.12Z"
                          fill="#165FE3"
                        />
                      </svg>
                    )}
                    {doc.status.toLowerCase() === "requested" && (
                      <svg
                        width="8"
                        height="14"
                        viewBox="0 0 8 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.29688 13H6.69695M3.99743 10V1M3.99743 1L6.62243 3.625M3.99743 1L1.37244 3.625"
                          stroke="#165FE3"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                ))}
                {
                  handleAddAdditionalDocument ?
                    <Button
                      variant="ghost"
                      onClick={() => setIsAddingDoc(true)}
                      className="w-full justify-start gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Additional Document
                    </Button>
                  :
                                    <RequestOrderDoc
                    refetchOrderDetails={refetchOrderDetails}
                    order_id={id as string}
                    {...(onRequest && {
                      onSubmit: (id) => {
                        onRequest(id);
                        setCurrentDocIndex(documents.length);
                      },
                    })}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Additional Document
                    </Button>
                  </RequestOrderDoc>
                }
              </div>

              {/* Main Content */}
              <div className="flex flex-col h-full">
                {isAddingDoc ? (
                  <div className="flex-1">
                    <AddDocumentNameInput docs={docs} setDocs={setDocs} />
                  </div>
                ) : currentDoc?.status.toLowerCase() === "requested" ? (
                  loadedFile?.name ? (
                    <div className="bg-[#F9F9F9] flex items-center flex-col justify-center h-full border rounded-lg cursor-pointer flex-1 overflow-hidden">
                      {loadedFile.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(loadedFile)}
                          alt={loadedFile.name}
                          className=" w-full h-full object-contain"
                        />
                      ) : (
                        <div data-testid="file-chip" className="flex flex-col items-center gap-1 rounded-lg border bg-white-A700 px-6 py-4 text-sm text-[#333333]">
                          <span className="font-medium break-all">{loadedFile.name}</span>
                          <span className="text-[#555555]">{`${loadedFile.type === "application/pdf" ? "PDF" : "File"}, ${(loadedFile.size / 1024).toFixed(loadedFile.size < 10240 ? 1 : 0)} KB`}</span>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setLoadedFile(null);
                        }}
                        className="text-red-700 text-sm font-medium font-inter leading-[16.9px] p-2"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="bg-[#F9F9F9] flex items-center flex-col justify-center h-full border rounded-lg cursor-pointer">
                      <div className="flex items-center flex-col justify-center">
                        <svg
                          width="13"
                          height="12"
                          viewBox="0 0 13 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.50001 0.445312C5.40139 0.445313 4.32745 0.771089 3.41398 1.38145C2.50052 1.9918 1.78856 2.85933 1.36814 3.87431C0.947719 4.8893 0.837718 6.00616 1.05205 7.08367C1.26638 8.16117 1.79541 9.15092 2.57225 9.92776C3.34908 10.7046 4.33883 11.2336 5.41634 11.448C6.49384 11.6623 7.61071 11.5523 8.62569 11.1319C9.64068 10.7114 10.5082 9.99949 11.1186 9.08602C11.7289 8.17256 12.0547 7.09862 12.0547 6C12.0531 4.52728 11.4674 3.11533 10.426 2.07396C9.38468 1.03259 7.97272 0.446868 6.50001 0.445312ZM6.28636 3.00902C6.41313 3.00902 6.53704 3.04661 6.64244 3.11703C6.74784 3.18746 6.82999 3.28756 6.8785 3.40467C6.92701 3.52178 6.9397 3.65065 6.91497 3.77498C6.89024 3.89931 6.8292 4.01351 6.73957 4.10314C6.64993 4.19278 6.53573 4.25382 6.4114 4.27855C6.28707 4.30328 6.15821 4.29059 6.04109 4.24208C5.92398 4.19357 5.82388 4.11142 5.75345 4.00602C5.68303 3.90062 5.64544 3.7767 5.64544 3.64994C5.64544 3.47996 5.71296 3.31694 5.83316 3.19674C5.95336 3.07654 6.11638 3.00902 6.28636 3.00902ZM6.92729 8.99099C6.70064 8.99099 6.48328 8.90096 6.32302 8.74069C6.16276 8.58043 6.07272 8.36307 6.07272 8.13642V6C5.9594 6 5.85072 5.95499 5.77059 5.87485C5.69045 5.79472 5.64544 5.68604 5.64544 5.57272C5.64544 5.4594 5.69045 5.35072 5.77059 5.27058C5.85072 5.19045 5.9594 5.14544 6.07272 5.14544C6.29937 5.14544 6.51673 5.23547 6.67699 5.39573C6.83725 5.55599 6.92729 5.77336 6.92729 6V8.13642C7.04061 8.13642 7.14929 8.18144 7.22942 8.26157C7.30956 8.3417 7.35457 8.45038 7.35457 8.56371C7.35457 8.67703 7.30956 8.78571 7.22942 8.86584C7.14929 8.94597 7.04061 8.99099 6.92729 8.99099Z"
                            fill="#A6ABB4"
                          />
                        </svg>
                        <p className="">
                          The file can be a pdf or image (png, jpg, jpeg) with
                          max size of 5MB.
                        </p>
                        <p className="">
                          Only a single file upload is allowed.
                        </p>
                      </div>
                      <label
                        htmlFor="file"
                        className=" border border-blue-700 rounded-md px-4 py-2 mt-4 cursor-pointer"
                      >
                        <input
                          type="file"
                          name=""
                          id="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e?.target?.files?.[0];
                            if (file && file?.size >= 5 * 1024 * 1024) {
                              toast.error(
                                "File size should be less than or equal to 5MB",
                              );
                              return;
                            }
                            setLoadedFile(e.target.files?.[0] as File);
                          }}
                          accept=".pdf,image/*"
                        />
                        <span className="text-blue-700">Upload Document</span>
                      </label>
                    </div>
                  )
                ) : (
                  <div className="flex flex-1 flex-col gap-2">
                    <FilePreview
                      key={previewKey}
                      url={currentDoc?.url}
                      kind={currentDoc?.type === "pdf" ? "pdf" : "image"}
                      name={currentDoc?.name ?? "Document"}
                      onState={(state) => setPreviewState({ key: previewKey, state })}
                    />
                    {currentDoc?.url ? <PreviewNote state={previewState.key === previewKey ? previewState.state : "loading"} /> : null}
                  </div>
                )}

              </div>
            </div>
            <div className="shrink-0 border-t bg-white-A700 px-4 pb-4 pt-1">
                {!isAddingDoc ? (
                  currentDoc?.status.toLowerCase() === "requested" ? (
                    <div className="flex items-center justify-end mt-4">
                      <Button
                        className="bg-blue-700 rounded-md text-white-A700 border border-blue-700"
                        onClick={handleApprove}
                        disabled={loadedFile === null}
                      >
                        Save
                      </Button>
                    </div>
                  ) : rejecting ? (
                    <div className="mt-4 flex flex-col gap-2">
                      <label htmlFor="reject-reason" className="text-sm font-medium">
                        Why is this document rejected?
                      </label>
                      <textarea
                        id="reject-reason"
                        className="min-h-[72px] w-full rounded-md border border-gray-300 p-2 text-sm"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" type="button" onClick={() => { setRejecting(false); setRejectReason(""); }}>
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          className="bg-[#C62828] text-white-A700"
                          disabled={rejectReason.trim().length < 3}
                          onClick={handleReject}
                        >
                          Reject document
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Previous document"
                          onClick={handlePrevious}
                          disabled={currentDocIndex === 0}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          aria-label="Next document"
                          onClick={handleNext}
                          disabled={currentDocIndex === documents.length - 1}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleReUpload}
                          accept=".pdf,image/*"
                        />
                        {!(currentDoc?.status.toLowerCase() === "verified" && canReplaceVerified === false) && (
                        <Button
                          className={`text-blue-700 ${checkLevel(LEVEL_2) ? "border border-blue-700 rounded-lg" : " border-none"}`}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={(currentDoc?.status.toLowerCase() === "verified" && canReplaceVerified !== true) || documents?.length === 0}
                        >
                          <RefreshCcw className="h-4 w-4 mr-2 border-none" />
                          Re-Upload
                        </Button>
                        )}

                        {onReject && (
                          <Button
                            variant="outline"
                            type="button"
                            disabled={currentDoc?.status.toLowerCase() === "verified" || documents?.length === 0}
                            onClick={() => setRejecting(true)}
                            className="border rounded-md border-[#C62828] text-[#C62828]"
                          >
                            Reject
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          type="button"
                          disabled={currentDoc?.status.toLowerCase() === "verified" || documents?.length === 0}
                          onClick={handleRequest}
                          className="border rounded-md border-blue-700 text-blue-700"
                        >
                          Request
                        </Button>
                        {(allDocsVerified || canApprove !== false) && (
                        <Button
                          className="bg-blue-700 disabled:opacity-40 rounded-md text-white-A700 border border-blue-700"
                          onClick={() => {
                            if (allDocsVerified) {
                              setIsOpen(false);
                              onSubmit?.(parseInt(id as string, 10));
                              return;
                            }
                            handleApprove();
                          }}
                          disabled={
                            (allDocsVerified
                              ? false
                              : currentDoc?.status.toLowerCase() === "verified" || !previewLoaded)
                              || documents?.length === 0
                          }
                        >
                          {allDocsVerified ? (onSubmit ? "Submit" : "Done") : "Approve"}
                        </Button>
                        )}
                      </div>
                    </div>
                  )
                )
              :
                  <div className="flex items-center justify-end mt-4">
                    <Button
                      className="bg-blue-700 rounded-md text-white-A700 border border-blue-700"
                      onClick={async ()=>{
                        if(docs.some(doc => !doc.file)) {
                          toast.error("Please upload all documents before submitting");
                          return;
                        }
                        try {
                          handleAddAdditionalDocument?.(docs)
                          setIsAddingDoc(false);
                          setDocs([]);
                        } catch (error) { 
                          
                        }
                      }}
                      disabled={docs.length === 0}
                    >
                      Submit
                    </Button>
                  </div>
                }
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
}


const AddDocumentNameInput = ({docs, setDocs}:{
  docs:{
    id: string;
    name: string;
    file: File | null;
  }[];
  setDocs: React.Dispatch<React.SetStateAction<{
    id: string;
    name: string;
    file: File | null;
  }[]>>;
}) => {
  const [newDocumentName, setNewDocumentName] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleAddDocumentToList = () => {
    if (docs.find(doc => doc.name === newDocumentName)?.name) {
      toast.error("Document with this name already exists");
      return;
    }
    if (newDocumentName.trim()) {
      setDocs((prevDocs) => [
        ...prevDocs,
        { id: crypto.randomUUID(), name: newDocumentName.trim(), file: null },
      ]);
      setNewDocumentName("");
    }
  }
  const handleFileChange = (docName: string, docId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file?.size >= 5 * 1024 * 1024) {
      toast.error(
        "File size should be less than or equal to 5MB",
      );
      return;
    }
    if (file) {
      setDocs((prevDocs) =>
        prevDocs.map((doc) =>
          doc.id === docId ? { ...doc, file } : doc
        )
      );
    }
  };

  const handleRemoveDocument = (docId: string) => {
    setDocs((prevDocs) => prevDocs.filter((doc) => doc.id !== docId));
  };

  return (
    <div className="max-w-md space-y-8">
        <h3 className="text-lg font-semibold text-gray-900">Add Additional Document</h3>          
      <div className="space-y-3">
        <div>
          <label className=" text-sm font-medium flex items-center justify-between text-gray-700 mb-2">
            Name the document
            <button type='button' onClick={()=>setNewDocumentName('')} className="underline text-blue-700 font-semibold"> Cancel</button>
          </label>
          <div className="relative">
            <input
              type="text"
              value={newDocumentName}
              onChange={(e) => setNewDocumentName(e.target.value)}
              placeholder="Enter the document name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"              
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddDocumentToList}
              disabled={!newDocumentName.trim()}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      

        {docs.map(({ id, file, name }) => (
          <div
            key={id}
            className="px-3 py-2 flex items-center justify-between even:bg-[#FAFAFA] rounded-md"
          >
            <div className="flex items-center gap-3">
              {file?.name ? (
                <img
                  src="/images/login/green-tick.svg"
                  alt="not completed"
                  className="w-5 h-5"
                />
              ) : (
                <img
                  src="/images/remit/grayTick.svg"
                  alt="completed"
                  className="w-5 h-5"
                />
              )}
              <div>
                <h3 className="text-sm font-medium text-clip  text-gray-800">
                  {name ?? `Upload ${name}`}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {file?.name && (
                <a
                  href={URL.createObjectURL(file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline underline-offset-2 text-sm"
                >
                  View
                </a>
              )}
              <label
                htmlFor={id}
                className="text-blue-700 whitespace-nowrap border border-blue-700 cursor-pointer px-3 py-1.5 rounded-lg text-sm"
              >
                {file?.name ? "Re-upload" : "Upload"}
              </label>
              <input
                type="file"
                id={id}
                className="hidden"
                accept="image/*,application/pdf"
                onChange={(e) => {
                  handleFileChange(name, id)(e);
                }}
              />

              {/* Remove button */}
              <button onClick={() => handleRemoveDocument(id)} className="">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.75705 4.86316L6.44084 16.3562C6.45114 16.5275 6.52641 16.6884 6.65129 16.8061C6.77617 16.9238 6.94125 16.9894 7.11284 16.9895H13.2029C13.3745 16.9894 13.5396 16.9238 13.6645 16.8061C13.7894 16.6884 13.8647 16.5275 13.8749 16.3562L14.5587 4.86316H5.75705ZM15.5718 4.86316L14.8838 16.416C14.8582 16.8445 14.67 17.2471 14.3576 17.5415C14.0452 17.8359 13.6322 17.9999 13.2029 18H7.11284C6.68358 17.9999 6.27055 17.8359 5.95814 17.5415C5.64574 17.2471 5.45754 16.8445 5.432 16.416L4.744 4.86316H3V4.27368C3 4.16201 3.04436 4.05492 3.12332 3.97596C3.20229 3.89699 3.30938 3.85263 3.42105 3.85263H16.8947C17.0064 3.85263 17.1135 3.89699 17.1925 3.97596C17.2714 4.05492 17.3158 4.16201 17.3158 4.27368V4.86316H15.5718ZM11.8421 2C11.9538 2 12.0609 2.04436 12.1398 2.12332C12.2188 2.20229 12.2632 2.30938 12.2632 2.42105V3.01053H8.05263V2.42105C8.05263 2.30938 8.09699 2.20229 8.17595 2.12332C8.25492 2.04436 8.36201 2 8.47368 2H11.8421ZM8.05263 7.05263H9.06316L9.48421 14.6316H8.47368L8.05263 7.05263ZM11.2526 7.05263H12.2632L11.8421 14.6316H10.8316L11.2526 7.05263Z"
                    fill="#FF0000"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}